import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { auth, signInWithGoogle, logout } from './firebase';
import ErrorBoundary from './components/ErrorBoundary';
import { onAuthStateChanged, User } from 'firebase/auth';
import { useIsMobile } from './hooks/useIsMobile';
import CreateBot from './pages/CreateBot';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import BotLayout from './pages/dashboard/BotLayout';
import OverviewRoute from './pages/dashboard/bot/OverviewRoute';
import ConversationsRoute from './pages/dashboard/bot/ConversationsRoute';
import UsersRoute from './pages/dashboard/bot/UsersRoute';
import OrdersRoute from './pages/dashboard/bot/OrdersRoute';
import BookingsRoute from './pages/dashboard/bot/BookingsRoute';
import ReportsRoute from './pages/dashboard/bot/ReportsRoute';
import RealtimeRoute from './pages/dashboard/bot/RealtimeRoute';
import EmbedRoute from './pages/dashboard/bot/EmbedRoute';
import PostsRoute from './pages/dashboard/bot/PostsRoute';
import CommentsRoute from './pages/dashboard/bot/CommentsRoute';
import InsightsRoute from './pages/dashboard/bot/InsightsRoute';
import DashboardMobile from './pages/mobile/DashboardMobile';
import BillingMobile from './pages/mobile/BillingMobile';
import AccountMobile from './pages/mobile/AccountMobile';
import CampaignsMobile from './pages/mobile/CampaignsMobile';
import SupportMobile from './pages/mobile/SupportMobile';
import DocumentationMobile from './pages/mobile/DocumentationMobile';
import CreateBotMobile from './pages/mobile/CreateBotMobile';
import Home from './pages/Home';
import './App.css';
import CreateVoiceBot from './pages/CreateVoiceBot';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';

import Register from './pages/Register';
import Billing from './pages/Billing';
import Documentation from './pages/Documentation';
import ErrorDatabase from './pages/ErrorDatabase';
import Datasets from './pages/Datasets';
import MediaLibrary from './pages/MediaLibrary';
import Database from './pages/Database';
import TemplateSelector from './components/TemplateSelector'
import Support from './pages/Support';
import Account from './pages/Account';
import FlowBuilderRouter from './pages/FlowBuilderRouter';
import WhatsAppBuilder from './pages/WhatsAppBuilder';
import FacebookBuilder from './pages/FacebookBuilder';
import VoiceBuilder from './pages/VoiceBuilder';
import WebsiteBuilder from './pages/WebsiteBuilder';
import About from './pages/About';
import Contact from './pages/Contact';
import Pricing from './pages/pricing';
import Renew from './pages/Renew';
import HowItWorks from './pages/Howitworks';


import { useCookieConsent, CookieConsent } from './components/CookieConsent';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DataDeletion from './pages/DataDeletion';
import { AcceptableUsePolicy, CookiePolicy } from './pages/Policies';
import ResetPassword from './pages/ResetPassword';
import Campaigns from './pages/Campaigns';

function App() {
  const isMobile = useIsMobile();
  const consent = useCookieConsent();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(!auth.currentUser);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    }, (error) => {
      console.error("Auth state error:", error);
      setError(error.message);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleSignIn = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      console.error("Sign in error:", err);

      if (err.code === 'auth/configuration-not-found') {
        setError('Google Sign-In not enabled. Please enable it in Firebase Console.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups for this site.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('Domain not authorized. Add localhost to Firebase authorized domains.');
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }



  return (
    <ThemeProvider>
    <ErrorBoundary>
    <BrowserRouter>
      <CookieConsent consent={consent} />
      <div>
        <div className="container">
          <Routes>
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />


            <Route path="/datasets" element={<Datasets />} />
            <Route path="/media" element={<MediaLibrary />} />
            <Route path="/database" element={<Database />} />

            <Route path="/template-builder/whatsapp/:botId" element={<WhatsAppBuilder />} />
            <Route path="/template-builder/website/:botId" element={<WebsiteBuilder />} />
            <Route path="/template-builder/voice/:botId" element={<VoiceBuilder />} />
            <Route path="/template-builder/facebook/:botId" element={<FacebookBuilder />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/support" element={isMobile ? <SupportMobile /> : <Support />} />
            <Route path="/account" element={isMobile ? <AccountMobile /> : <Account />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/renew" element={<Renew />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/create-voice-bot" element={<CreateVoiceBot />} />
            <Route path="/billing" element={isMobile ? <BillingMobile /> : <Billing />} />
            <Route path="/documentation" element={isMobile ? <DocumentationMobile /> : <Documentation />} />
            <Route path="/errors" element={<ErrorDatabase />} />
            <Route path="/register" element={<Register />} />
            <Route path="/builder/:botId" element={<FlowBuilderRouter />} />
            <Route path="/builder/whatsapp/:botId" element={<WhatsAppBuilder />} />
            <Route path="/builder/facebook/:botId" element={<FacebookBuilder />} />
            <Route path="/builder/website/:botId" element={<WebsiteBuilder />} />
            <Route path="/builder/voice/:botId" element={<VoiceBuilder />} />
            <Route path="/" element={<Home consent={consent} />} />
            {isMobile ? (
              <Route path="/dashboard" element={<DashboardMobile />} />
            ) : (
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashboardHome />} />
                <Route path="bots/:botId" element={<BotLayout />}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<OverviewRoute />} />
                  <Route path="conversations" element={<ConversationsRoute />} />
                  <Route path="users" element={<UsersRoute />} />
                  <Route path="orders" element={<OrdersRoute />} />
                  <Route path="bookings" element={<BookingsRoute />} />
                  <Route path="reports" element={<ReportsRoute />} />
                  <Route path="realtime" element={<RealtimeRoute />} />
                  <Route path="embed" element={<EmbedRoute />} />
                  <Route path="posts" element={<PostsRoute />} />
                  <Route path="comments" element={<CommentsRoute />} />
                  <Route path="insights" element={<InsightsRoute />} />
                </Route>
              </Route>
            )}
            <Route path="/campaigns/:botId" element={isMobile ? <CampaignsMobile /> : <Campaigns />} />
            <Route path="/create" element={isMobile ? <CreateBotMobile /> : <CreateBot />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </BrowserRouter>
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;